<script>
import Layout from "../../layouts/main.vue";
import PageHeader from "@/components/page-header";
import appConfig from "../../../app.config";
import axios from "axios";
import { usePermissions } from '@/composables/usePermissions';

export default {
  page: {
    title: "Role Management",
    meta: [{ name: "description", content: appConfig.description }],
  },
  components: { Layout, PageHeader },
  data() {
    return {
      title: "Role Management",
      items: [
        { text: "System", href: "/" },
        { text: "Role Management", active: true },
      ],
      roles: [],
      loadingRoles: false,
      allMenus: [],
      loadingMenus: false,
      form: {
        id: null,
        name: "",
        permissions: {},
      },
      isEditing: false,
      selectedRoleId: null,
      allUsers: [],
      roleUsers: [],
      selectedUsers: [],
      activeTab: "roles",
      loading: false,

      // Modal control
      showRoleModal: false,
      showAssignUsersModal: false,

      // Accordion control (replacing Bootstrap collapse)
      expandedMenus: {},   // menuId → boolean
    };
  },
    setup() {
        const {
            canReadRole,
            canUpdateRole,
            canCreateRole,
            canDeleteRole
        } = usePermissions()
        return {
            canReadRole,
            canUpdateRole,
            canCreateRole,
            canDeleteRole
        };
    },
  methods: {
    // ──────────────────────────────────────────────
    // Modal controls
    // ──────────────────────────────────────────────
    openNewRole() {
      this.isEditing = false;
      this.form = { id: null, name: "", permissions: {} };
      this.showRoleModal = true;
    },

    editRole(role) {
      this.isEditing = true;
      this.form = { id: role.id, name: role.name, permissions: {} };
      this.showRoleModal = true;
    },

    closeRoleModal() {
      this.showRoleModal = false;
    },

    openAssignUsers(role) {
      this.form = { ...role };
      this.fetchAllUsers();
      this.fetchRoleUsers(role.id);
      this.showAssignUsersModal = true;
    },

    closeAssignUsersModal() {
      this.showAssignUsersModal = false;
    },

    toggleMenu(menuId) {
      this.expandedMenus[menuId] = !this.expandedMenus[menuId];
    },

    isMenuExpanded(menuId) {
      return !!this.expandedMenus[menuId];
    },
    safeHasOwnProperty(obj, prop) {
      return Object.prototype.hasOwnProperty.call(obj, prop);
    },

    async fetchRoles() {
      this.loadingRoles = true;
      try {
        const res = await axios.get(`${process.env.VUE_APP_API_BASEURL}roles`);
        this.roles = res.data || [];
      } catch (err) {
        console.error("Failed to load roles", err);
        alert("Could not load roles. Please try again.");
      } finally {
        this.loadingRoles = false;
      }
    },

    async saveRole() {
      if (!this.form.name.trim()) {
        alert("Role name is required");
        return;
      }
      this.loading = true;
      try {
        const payload = { name: this.form.name.trim() };
        if (this.isEditing) {
          await axios.put(`${process.env.VUE_APP_API_BASEURL}roles/${this.form.id}`, payload);
          alert("Role updated successfully");
        } else {
          const res = await axios.post(`${process.env.VUE_APP_API_BASEURL}roles`, payload);
          alert("Role created successfully");
          this.roles.push(res.data);
        }
        this.closeRoleModal();
        await this.fetchRoles();
      } catch (err) {
        console.error(err);
        alert(err.response?.data?.message || "Failed to save role");
      } finally {
        this.loading = false;
      }
    },

    async deleteRole(id) {
      if (!confirm("Delete this role? Users assigned to it will lose these permissions.")) return;
      try {
        await axios.delete(`${process.env.VUE_APP_API_BASEURL}roles/${id}`);
        alert("Role deleted");
        await this.fetchRoles();
      } catch (err) {
        console.error(err);
        alert("Role Should be Empty. Please remove users from this role");
      }
    },

    async fetchAllMenus() {
      this.loadingMenus = true;
      try {
        const res = await axios.get(`${process.env.VUE_APP_API_BASEURL}roles/all-menus`);
        this.allMenus = res.data || [];
      } catch (err) {
        console.error("Failed to load menus", err);
        this.allMenus = [];
      } finally {
        this.loadingMenus = false;
      }
    },

    getDefaultPermissions() {
      const perms = {};
      this.allMenus.forEach((menu) => {
        perms[menu.id] = {
          read: false,
          create: false,
          update: false,
          delete: false,
          approve: false,
          request: false,
          export: false,
          submenus: {},
        };
        if (menu.submenus?.length) {
          menu.submenus.forEach((sub) => {
            perms[menu.id].submenus[sub.id] = {
              read: false,
              create: false,
              update: false,
              delete: false,
              approve: false,
              request: false,
              export: false,
            };
          });
        }
      });
      return perms;
    },

    async loadRolePermissions() {
      if (!this.selectedRoleId) {
        this.form.permissions = this.getDefaultPermissions();
        return;
      }
      try {
        const res = await axios.get(
          `${process.env.VUE_APP_API_BASEURL}roles/${this.selectedRoleId}/permissions`
        );
        const serverPerms = this.safeParsePermissions(res.data?.permissions || {});
        const defaultPerms = this.getDefaultPermissions();
        this.form.permissions = this.deepMergePermissions(defaultPerms, serverPerms);
      } catch (err) {
        console.error("Failed to load permissions", err);
        this.form.permissions = this.getDefaultPermissions();
      }
    },

    deepMergePermissions(target, source) {
      const merged = JSON.parse(JSON.stringify(target));
      Object.keys(source).forEach((key) => {
        if (merged[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
          Object.keys(source[key]).forEach((permKey) => {
            if (permKey !== 'submenus' && this.safeHasOwnProperty(merged[key], permKey)) {
              merged[key][permKey] = source[key][permKey];
            }
          });
          if (source[key].submenus && merged[key].submenus) {
            Object.keys(source[key].submenus).forEach((subKey) => {
              if (merged[key].submenus[subKey]) {
                Object.keys(source[key].submenus[subKey]).forEach((subPermKey) => {
                  if (this.safeHasOwnProperty(merged[key].submenus[subKey], subPermKey)) {
                    merged[key].submenus[subKey][subPermKey] = source[key].submenus[subKey][subPermKey];
                  }
                });
              }
            });
          }
        }
      });
      return merged;
    },

    safeParsePermissions(perms) {
      if (!perms) return {};
      if (typeof perms === 'object') return perms;
      if (typeof perms === 'string') {
        try {
          return JSON.parse(perms);
        } catch {
          return {};
        }
      }
      return {};
    },

    async fetchAllUsers() {
      try {
        const res = await axios.get(`${process.env.VUE_APP_API_BASEURL}user/list`);
        this.allUsers = res.data || [];
      } catch (err) {
        console.error('Cannot load users', err);
        this.allUsers = [];
      }
    },

    async fetchRoleUsers(roleId) {
      try {
        const res = await axios.get(`${process.env.VUE_APP_API_BASEURL}roles/${roleId}/users`);
        this.roleUsers = res.data || [];
        this.selectedUsers = res.data.map(u => u.id);
      } catch (err) {
        console.error('Cannot load role users', err);
        this.roleUsers = [];
        this.selectedUsers = [];
      }
    },

    async assignUsers() {
      if (!this.form.id) {
        alert("No role selected");
        return;
      }
      this.loading = true;
      try {
        await axios.post(
          `${process.env.VUE_APP_API_BASEURL}roles/${this.form.id}/assign-users`,
          { user_ids: this.selectedUsers }
        );
        alert("Users assigned successfully");
        this.closeAssignUsersModal();
        await this.fetchRoles();
      } catch (err) {
        console.error(err);
        alert(err.response?.data?.message || "Failed to assign users");
      } finally {
        this.loading = false;
      }
    },

    grantAllForMenu(menuId) {
      if (!this.form.permissions[menuId]) return;
      const perms = ["read", "create", "update", "delete", "approve", "request", "export"];
      perms.forEach(p => {
        this.form.permissions[menuId][p] = true;
      });
      const menu = this.allMenus.find(m => m.id === menuId);
      if (menu?.submenus && this.form.permissions[menuId].submenus) {
        menu.submenus.forEach(sub => {
          perms.forEach(p => {
            if (this.form.permissions[menuId].submenus[sub.id]) {
              this.form.permissions[menuId].submenus[sub.id][p] = true;
            }
          });
        });
      }
    },

    revokeAllForMenu(menuId) {
      if (!this.form.permissions[menuId]) return;
      const perms = ["read", "create", "update", "delete", "approve", "request", "export"];
      perms.forEach(p => {
        this.form.permissions[menuId][p] = false;
      });
      if (this.form.permissions[menuId].submenus) {
        Object.keys(this.form.permissions[menuId].submenus).forEach(subId => {
          perms.forEach(p => {
            this.form.permissions[menuId].submenus[subId][p] = false;
          });
        });
      }
    },

    togglePermission(menuId, perm) {
      if (this.form.permissions[menuId] && this.safeHasOwnProperty(this.form.permissions[menuId], perm)) {
        // No $set needed in Vue 3
        this.form.permissions[menuId][perm] = !this.form.permissions[menuId][perm];
      }
    },

    toggleSubmenuPermission(menuId, subId, perm) {
      if (
        this.form.permissions[menuId]?.submenus?.[subId] &&
        this.safeHasOwnProperty(this.form.permissions[menuId].submenus[subId], perm)
      ) {
        // No $set needed in Vue 3
        this.form.permissions[menuId].submenus[subId][perm] =
          !this.form.permissions[menuId].submenus[subId][perm];
      }
    },

    async savePermissions() {
      if (!this.selectedRoleId) {
        alert("Please select a role first");
        return;
      }
      this.loading = true;
      try {
        await axios.put(
          `${process.env.VUE_APP_API_BASEURL}roles/${this.selectedRoleId}`,
          { permissions: this.form.permissions }
        );
        alert("Permissions saved successfully!");
      } catch (err) {
        console.error(err);
        alert(err.response?.data?.message || "Failed to save permissions");
      } finally {
        this.loading = false;
      }
    },
  },

  computed: {
    filteredUsers() {
      return this.allUsers.filter(u => !this.selectedUsers.includes(u.id));
    },
    currentRoleName() {
      if (!this.selectedRoleId) return "";
      const role = this.roles.find(r => r.id === this.selectedRoleId);
      return role ? role.name : "";
    },
  },

  watch: {
    selectedRoleId(newVal) {
      if (newVal) {
        this.loadRolePermissions();
      } else {
        this.form.permissions = this.getDefaultPermissions();
      }
    },
    activeTab(newTab) {
      if (newTab === 'permissions' && !this.allMenus.length) {
        this.fetchAllMenus();
      }
    },
  },

  mounted() {
    this.fetchRoles();
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />

    <div class="card" v-if="canReadRole">
      <div class="card-body">
        <ul class="nav nav-tabs mb-4" style="border-bottom: 1px solid #dee2e6;">
          <li class="nav-item">
            <button
              type="button"
              class="nav-link"
              :class="{ active: activeTab === 'roles' }"
              @click="activeTab = 'roles'"
            >
              Roles
            </button>
          </li>
          <li class="nav-item">
            <button
              type="button"
              class="nav-link"
              :class="{ active: activeTab === 'permissions' }"
              @click="activeTab = 'permissions'"
            >
              Permissions
            </button>
          </li>
        </ul>

        <!-- Roles Tab -->
        <div v-if="activeTab === 'roles'">
          <div class="d-flex justify-content-between align-items-center mb-4">
            <h5 class="mb-0">Roles List</h5>
            <button v-if="canCreateRole" class="btn btn-primary" @click="openNewRole">
              <i class="ri-add-line me-1"></i> Add New Role
            </button>
          </div>

          <div v-if="loadingRoles" class="text-center py-5">
            <div class="spinner-border text-primary" role="status"></div>
            <p class="mt-2">Loading roles...</p>
          </div>

          <div v-else-if="roles.length === 0" class="text-center py-5 text-muted">
            <i class="ri-user-shared-line ri-4x d-block mb-3"></i>
            <p>No roles found. Create your first role.</p>
          </div>

          <div v-else class="table-responsive">
            <table class="table table-striped table-hover">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Role Name</th>
                  <th>Users</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="role in roles" :key="role.id">
                  <td>{{ role.id }}</td>
                  <td class="fw-semibold">{{ role.name }}</td>
                  <td>
                    <span class="badge bg-primary rounded-pill">{{ role.users_count || 0 }}</span>
                  </td>
                  <td>
                    <button v-if="canUpdateRole" class="btn btn-sm btn-outline-info me-1" @click="editRole(role)">
                      <i class="ri-edit-line"></i>
                    </button>
                    <button v-if="canUpdateRole" class="btn btn-sm btn-outline-success me-1" @click="openAssignUsers(role)">
                      <i class="ri-user-add-line"></i>
                    </button>
                    <button v-if="canDeleteRole" class="btn btn-sm btn-outline-danger" @click="deleteRole(role.id)">
                      <i class="ri-delete-bin-line"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Permissions Tab -->
        <div v-if="activeTab === 'permissions' && canUpdateRole">
          <div class="row mb-4 align-items-center">
            <div class="col-md-6">
              <h5 class="mb-0">Edit Permissions</h5>
              <p v-if="selectedRoleId" class="text-muted small">
                Editing permissions for: <strong>{{ currentRoleName }}</strong>
              </p>
            </div>
            <div class="col-md-6 text-end">
              <select class="form-select d-inline-block me-2 w-auto" v-model="selectedRoleId">
                <option value="">Select Role</option>
                <option v-for="r in roles" :key="r.id" :value="r.id">
                  {{ r.name }} ({{ r.users_count || 0 }} users)
                </option>
              </select>
              <button
              v-if="canUpdateRole"
                class="btn btn-success"
                @click="savePermissions"
                :disabled="!selectedRoleId || loading"
              >
                <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
                Save Permissions
              </button>
            </div>
          </div>

          <div v-if="!selectedRoleId" class="text-center py-5 text-muted">
            <i class="ri-shield-keyhole-line ri-4x d-block mb-3"></i>
            <p>Please select a role to edit permissions</p>
          </div>

          <div v-else-if="loadingMenus" class="text-center py-5">
            <div class="spinner-border text-primary"></div>
            <p class="mt-2">Loading permissions...</p>
          </div>

          <div v-else-if="allMenus.length === 0" class="text-center py-5 text-muted">
            <i class="ri-menu-line ri-4x d-block mb-3"></i>
            <p>No menus found for permissions configuration.</p>
          </div>

          <div v-else>
            <div v-for="menu in allMenus" :key="menu.id" class="card mb-3">
              <div
                class="card-header d-flex justify-content-between align-items-center"
                style="cursor: pointer;"
                @click="toggleMenu(menu.id)"
              >
                <div>
                  <i :class="menu.tab_icon + ' me-2'"></i>
                  {{ menu.tab_name || menu.name }}
                </div>
                <div class="btn-group btn-group-sm">
                  <button class="btn btn-outline-success btn-sm" @click.stop="grantAllForMenu(menu.id)">
                    Grant All
                  </button>
                  <button class="btn btn-outline-danger btn-sm" @click.stop="revokeAllForMenu(menu.id)">
                    Revoke All
                  </button>
                </div>
              </div>

              <div v-if="isMenuExpanded(menu.id)" class="card-body">
                <h6 class="mb-3 text-primary">Main Menu Permissions</h6>
                <div class="row g-3 mb-4">
                  <div
                    v-for="perm in ['read','create','update','delete','approve','request','export']"
                    :key="perm"
                    class="col-6 col-md-4 col-lg-3"
                  >
                    <div class="form-check form-switch">
                      <input
                        class="form-check-input"
                        type="checkbox"
                        :id="`main-${menu.id}-${perm}`"
                        :checked="form.permissions[menu.id]?.[perm] || false"
                        @change="togglePermission(menu.id, perm)"
                      />
                      <label class="form-check-label text-capitalize" :for="`main-${menu.id}-${perm}`">
                        {{ perm }}
                      </label>
                    </div>
                  </div>
                </div>

                <div v-if="menu.submenus?.length">
                  <h6 class="mb-3 text-primary">Submenu Permissions</h6>
                  <div class="table-responsive">
                    <table class="table table-sm table-bordered">
                      <thead class="table-light">
                        <tr>
                          <th>Submenu</th>
                          <th class="text-center">Read</th>
                          <th class="text-center">Create</th>
                          <th class="text-center">Update</th>
                          <th class="text-center">Delete</th>
                          <th class="text-center">Approve</th>
                          <th class="text-center">Request</th>
                          <th class="text-center">Export</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="sub in menu.submenus" :key="sub.id">
                          <td>{{ sub.tab_name || sub.name }}</td>
                          <td
                            v-for="perm in ['read','create','update','delete','approve','request','export']"
                            :key="perm"
                            class="text-center"
                          >
                            <input
                              type="checkbox"
                              :checked="form.permissions[menu.id]?.submenus?.[sub.id]?.[perm] || false"
                              @change="toggleSubmenuPermission(menu.id, sub.id, perm)"
                            />
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                <div v-else class="text-muted text-center py-3 fst-italic">
                  No submenus available
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
        <div v-else class="text-center text-muted p-4">
            You do not have permission to view Roles/permission.
        </div>
    <!-- Role Modal -->
    <div v-if="showRoleModal" class="modal-overlay" @click.self="closeRoleModal">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ isEditing ? 'Edit Role' : 'Add New Role' }}</h5>
            <button type="button" class="btn-close" @click="closeRoleModal"></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="saveRole">
              <div class="mb-3">
                <label for="roleName" class="form-label fw-semibold">Role Name *</label>
                <input
                  id="roleName"
                  v-model="form.name"
                  type="text"
                  class="form-control"
                  required
                  placeholder="e.g. Administrator, Editor, Viewer..."
                  :disabled="loading"
                />
              </div>
              <div class="d-flex justify-content-end gap-2">
                <button type="button" class="btn btn-light" @click="closeRoleModal" :disabled="loading">
                  Cancel
                </button>
                <button type="submit" class="btn btn-primary" :disabled="loading || !form.name.trim()">
                  {{ isEditing ? 'Update Role' : 'Create Role' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>

    <!-- Assign Users Modal -->
    <div v-if="showAssignUsersModal" class="modal-overlay" @click.self="closeAssignUsersModal">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Assign Users to {{ form.name || 'Selected Role' }}</h5>
            <button type="button" class="btn-close" @click="closeAssignUsersModal"></button>
          </div>
          <div class="modal-body">
            <div class="alert alert-info mb-4">
              Select users to assign them to this role. Click to move between lists.
            </div>
            <div class="row g-4">
              <div class="col-md-6">
                <h6 class="mb-3">Available Users ({{ filteredUsers.length }})</h6>
                <div class="list-group border" style="max-height: 300px; overflow-y: auto;">
                  <a
                    v-for="user in filteredUsers"
                    :key="user.id"
                    href="#"
                    class="list-group-item list-group-item-action"
                    @click.prevent="selectedUsers.push(user.id)"
                  >
                    <strong>{{ user.name }}</strong><br />
                    <small class="text-muted">{{ user.email }}</small>
                  </a>
                </div>
                <div v-if="!filteredUsers.length" class="text-center text-muted py-4">
                  All users are assigned
                </div>
              </div>

              <div class="col-md-6">
                <h6 class="mb-3">Assigned Users ({{ selectedUsers.length }})</h6>
                <div class="list-group border" style="max-height: 300px; overflow-y: auto;">
                  <a
                    v-for="user in allUsers.filter(u => selectedUsers.includes(u.id))"
                    :key="user.id"
                    href="#"
                    class="list-group-item list-group-item-action"
                    @click.prevent="selectedUsers = selectedUsers.filter(id => id !== user.id)"
                  >
                    <strong>{{ user.name }}</strong><br />
                    <small class="text-muted">{{ user.email }}</small>
                  </a>
                </div>
                <div v-if="!selectedUsers.length" class="text-center text-muted py-4">
                  No users assigned yet
                </div>
              </div>
            </div>

            <div class="d-flex justify-content-end gap-2 mt-4">
              <button type="button" class="btn btn-light" @click="closeAssignUsersModal" :disabled="loading">
                Cancel
              </button>
              <button type="button" class="btn btn-primary" @click="assignUsers" :disabled="loading">
                Assign Users
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}

.modal-dialog {
  max-width: 500px;
  width: 90%;
  background: white;
  border-radius: 8px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
}

.modal-content {
  border: none;
}

.modal-header {
  border-bottom: 1px solid #dee2e6;
  padding: 1rem;
}

.modal-body {
  padding: 1.5rem;
}

.nav-link {
  padding: 0.75rem 1.5rem;
  color: #495057;
}

.nav-link.active {
  color: #0d6efd;
  border-bottom: 3px solid #0d6efd;
  background: transparent;
}

.card-header {
  background: #f8f9fa;
  font-weight: 500;
}

.form-check-input:checked {
  background-color: #0d6efd;
  border-color: #0d6efd;
}
</style>